import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import MeasuredBy from "../molecules/MeasuredBy";

import { BASE_SIZES } from "../../constants/modal";

import {
  disabledStylesContinue,
  activeStylesContinue,
  femaleBraScreenUI,
  welcomeScreenUI,
  font,
} from "../../configs";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";
import { isFootwear, isLingerie } from "../../utils/categoryParams";
import { findBrandByDomain } from "../../configs/configLoader";
import StyledSelector from "./components/StyledSelector";
import BaseSizeSelector from "./components/BaseSizeSelector";
import { handleAnalytics } from "../../utils/tracking";
import ShoesComponentSize from "./components/ShoesComponentSize";
import {
  MEDIUM_PADDING_BRANDS,
  SMALL_PADDING_BRANDS,
} from "../../constants/sizes";
import { hoverStylesContinue, titleStyles } from "../../configs/stylesLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";

import "./index.css";

interface IPropsStepBreasts {
  step: {
    number: number;
  } | null;
  value_1: {
    current: string | null;
  };
  value_2: {
    current: string | null;
  };
  nextStep: () => void;
  skipResult: () => void;
  selectedGender: string;
  sizeCountry: string;
  setSizeCountry: React.Dispatch<React.SetStateAction<string>>;
  descriptionStyles: React.CSSProperties;
  isSubmitting: boolean;
}

type BodyItemsType = "unfocused" | "focused";

const StepBreasts: FC<IPropsStepBreasts> = ({
  step,
  value_1,
  value_2,
  selectedGender,
  nextStep,
  skipResult,
  sizeCountry,
  setSizeCountry,
  descriptionStyles,
  isSubmitting,
}) => {
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(false);

  const { t } = useTranslation("components/breasts");

  const firstCupRef = useRef<HTMLButtonElement | null>(null);
  const firstSizeRef = useRef<HTMLButtonElement | null>(null);
  const continueButtonRef = useRef<HTMLButtonElement | null>(null);

  const [hoveredSize, setHoveredSize] = useState<string | null>(null);
  const [hoveredCup, setHoveredCup] = useState<string | null>(null);

  const [selectedSize, setSelectedSize] = useState<any | undefined>();
  const [selectedCup, setSelectedCup] = useState<any | undefined>();

  const [selectedSizeFR, setSelectedSizeFR] = useState<any | undefined>();
  const [selectedCupFR, setSelectedCupFR] = useState<any | undefined>();

  const [hovered, setHovered] = useState(false);

  const brandDefined = findBrandByDomain();

  const fontFamily = `${font}, sans-serif`;


  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isSRP = brandDefined?.name === "SRP";
  const isTheodore = brandDefined?.name === "Theodore";
  const isZadig = brandDefined?.name === "Zadig & Voltaire";
  const isPeserico = brandDefined?.name === "Peserico";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const isGivenchyDesktop = isGivenchy && !isMobile;

  const isSmallHeightDevice = useMediaQuery({
    maxHeight: isGivenchyDesktop ? 820 : 720,
    minWidth: 767,
  });

  useEffect(() => {
    if (selectedSize) {
      const sizeValue =
        sizeCountry === "FR"
          ? (selectedSize?.size as string | null) || null
          : (selectedSize?.fr as string | null) || null;

      setSelectedSizeFR(sizeValue);

      value_1.current = sizeValue ? sizeValue?.toString() : null;
    }

    if (selectedCup) {
      const cupValue =
        sizeCountry === "FR"
          ? (selectedCup?.size as string | null) || null
          : (selectedCup?.fr as string | null) || null;

      const cupValueFR = selectedCup?.fr as string | null;

      setSelectedCupFR(cupValue);

      value_2.current = cupValueFR ? cupValueFR?.toString() : null;
    }
  }, [sizeCountry, selectedSize, selectedCup, value_1, value_2]);

  useEffect(() => {
    if (selectedSize && !selectedCup) {
      requestAnimationFrame(() => {
        firstCupRef.current?.focus();
      });
    }
  }, [selectedSize, selectedCup]);

  useEffect(() => {
    if (
      typeof selectedSizeFR === "number" &&
      typeof selectedCupFR === "string"
    ) {
      setTimeout(() => {
        continueButtonRef.current?.focus();
      }, 0);
    }
  }, [selectedSizeFR, selectedCupFR]);

  const handlePaddingForBrand = (brandName: string) => {
    const smallPadding = "10px";
    const mediumPadding = "13px";

    if (brandName === "Zadig & Voltaire" || brandName === "Lacoste") {
      return "12px";
    }

    if (SMALL_PADDING_BRANDS.includes(brandName)) {
      return smallPadding;
    } else if (MEDIUM_PADDING_BRANDS.includes(brandName)) {
      return mediumPadding;
    } else {
      return smallPadding;
    }
  };

  const getBodyItemsStyles = (
    type: BodyItemsType,
    isHovered = false,
    isSelected = false,
  ) => {
    const padding = brandDefined?.name
      ? handlePaddingForBrand(brandDefined?.name)
      : "10px";

    const sizeSelectorItem = femaleBraScreenUI.sizeSelector;

    const isSizeUnderlined =
      isSelected &&
      (sizeSelectorItem["focused"] as any)?.textDecoration === "underline";

    const borderWidth = isSizeUnderlined
      ? "0 0 1px 0"
      : sizeSelectorItem[type].borderWidth;

    let borderColor = sizeSelectorItem[type].borderColor;
    const color = isSizeUnderlined
      ? "#000000"
      : sizeSelectorItem[type].fontColor;

    if (isIntersport && isHovered && !isSelected) {
      borderColor = "#A1A1A1";
    }

    return {
      padding,
      backgroundColor: isSizeUnderlined
        ? "transparent"
        : sizeSelectorItem[type].backgroundColor,
      borderRadius: sizeSelectorItem[type].borderRadius,
      borderWidth,
      borderStyle: "solid",
      borderColor,
      color,
      fontSize: sizeSelectorItem[type].fontSize,
      fontWeight: sizeSelectorItem[type]
        .fontWeight as React.CSSProperties["fontWeight"],
      textTransform: sizeSelectorItem[type]
        .textTransform as React.CSSProperties["textTransform"],
      height: `calc(${padding} * 3)`,
      fontFamily,
      letterSpacing: (sizeSelectorItem[type] as any)?.letterSpacing,
    };
  };

  const titlesStyles = {
    fontWeight: welcomeScreenUI.input_fields.title.fontWeight,
    textAlign: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["textAlign"],
    textTransform: welcomeScreenUI.input_fields.title
      .textTransform as React.CSSProperties["textTransform"],
    color: (isGivenchy
      ? titleStyles.color
      : welcomeScreenUI.input_fields.title
          .fontColor) as React.CSSProperties["color"],
    fontSize: isMobile
      ? "14px"
      : (welcomeScreenUI.input_fields.title
          .fontSize as React.CSSProperties["fontSize"]),
    justifyContent: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["justifyContent"],
    fontStyle: (welcomeScreenUI.input_fields.title as any)
      .fontStyle as React.CSSProperties["fontStyle"],
    fontFamily: (welcomeScreenUI.input_fields.title as any)
      .fontFamily as React.CSSProperties["fontFamily"],
    letterSpacing: (welcomeScreenUI.input_fields.title as any)?.letterSpacing,
  };

  const CTAStyles: React.CSSProperties = {
    ...(typeof selectedSizeFR !== "number" || typeof selectedCupFR !== "string"
      ? disabledStylesContinue
      : activeStylesContinue),
    ...(hovered && selectedSize ? hoverStylesContinue : {}),
    marginBottom: !isMobile && isVB ? "60px" : isMobile ? "20px" : "0px",
    marginTop: !isMobile && isVB ? "60px" : isMobile ? "20px" : "30px",
    textTransform:
      activeStylesContinue.textTransform === "capitalize"
        ? "none"
        : (activeStylesContinue.textTransform as React.CSSProperties["textTransform"]),
    padding: isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
    height: isVB ? "30px" : "auto",
    position: isGivenchyDesktop && !isSmallHeightDevice ? "fixed" : "relative",
    bottom: isGivenchyDesktop && !isSmallHeightDevice ? "200px" : 0,
    width:
      isGivenchyDesktop && !isSmallHeightDevice ? "calc(100% - 160px)" : "",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered && selectedSize ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered && selectedSize ? 1 : 0,
    transform: hovered && selectedSize ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  return isFootwear ? (
    <ShoesComponentSize
      step={step}
      value_1={value_1}
      nextStep={nextStep}
      selectedGender={selectedGender}
      sizeCountry={sizeCountry}
      setSizeCountry={setSizeCountry}
    />
  ) : (
    <div className="breasts">
      {!isVB ? (
        <p
          className="breasts-description"
          style={{
            ...descriptionStyles,
            textTransform:
              descriptionStyles.textTransform === "capitalize"
                ? "none"
                : (descriptionStyles.textTransform as React.CSSProperties["textTransform"]),
            marginBottom: isMobile ? "25px" : "15px",
            marginTop: isMobile && !isSRP ? "0" : "10px",
          }}
        >
          {descriptionStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("description"))
            : t("description")}
        </p>
      ) : null}
      <div
        className="breasts__titles"
        style={{
          display: "flex",
        }}
      >
        <p
          className="breasts__titles__title"
          style={{
            ...titlesStyles,
            fontSize: isGivenchy
              ? descriptionStyles.fontSize
              : titlesStyles.fontSize,
          }}
        >
          {titlesStyles.textTransform === "capitalize"
            ? capitalizeFirstLetter(t("pays"))
            : t("pays")}
        </p>
        <div onClick={() => setOpen(!open)}>
          {isSRP ? (
            <StyledSelector
              sizeCountry={sizeCountry}
              setSizeCountry={setSizeCountry}
              open={open}
              setOpen={setOpen}
            />
          ) : (
            <BaseSizeSelector
              sizeCountry={sizeCountry}
              setSizeCountry={setSizeCountry}
              open={open}
              setOpen={setOpen}
            />
          )}
        </div>
      </div>
      <div className="breasts__titles">
        <p
          id="sizeTitle"
          className="breasts__titles__title"
          style={{
            ...titlesStyles,
            textTransform:
              titlesStyles.textTransform === "capitalize"
                ? "none"
                : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
            fontSize: isGivenchy
              ? descriptionStyles.fontSize
              : titlesStyles.fontSize,
          }}
          dangerouslySetInnerHTML={{
            __html: isGivenchy
              ? t("size", {
                  value: selectedSize?.size
                    ? selectedSize?.size?.toString()?.toUpperCase()
                    : t("Select", { ns: "components/intro" }),
                })
              : titlesStyles.textTransform === "capitalize"
                ? capitalizeFirstLetter(t("size"))
                : t("size"),
          }}
        />
      </div>
      <div className="breasts__body">
        <table
          className="breasts__body__table"
          style={{
            marginBottom: isGivenchyDesktop ? "10px" : "",
          }}
        >
          <div className="sizes-grid">
            {BASE_SIZES.find((v) => v.name === sizeCountry)?.band_sizes.map(
              (row, index) => (
                <button
                  key={index}
                  tabIndex={0}
                  ref={index === 0 ? firstSizeRef : null}
                  aria-label={`${t("size")} ${row.size}`}
                  className={`size-button ${selectedSize?.size === row.size ? "selected" : ""}`}
                  onClick={() => setSelectedSize(row)}
                  onMouseEnter={() => setHoveredSize(index?.toString())}
                  onMouseLeave={() => setHoveredSize(null)}
                  style={Object.assign({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width:
                      row?.size?.toString()?.length > 2
                        ? "auto"
                        : `calc(${
                            getBodyItemsStyles(
                              selectedSize?.size === row?.size
                                ? "focused"
                                : "unfocused",
                            ).padding
                          } * 3)`,
                    minWidth: "fit-content",
                    ...getBodyItemsStyles(
                      selectedSize?.size === row?.size
                        ? "focused"
                        : "unfocused",
                      hoveredSize === index.toString(),
                      selectedSize?.size === row?.size,
                    ),
                    ...(!isMobile && (isIntersport || isBlackstore)
                      ? { minHeight: "48px", minWidth: "48px" }
                      : {}),
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    aspectRatio:
                      row?.size?.toString()?.length > 2 ? "unset" : "1 / 1",
                  })}
                >
                  {row.size}
                </button>
              ),
            )}
          </div>
        </table>
        <div className="breasts__titles">
          <p
            id="cupTitle"
            className="breasts__titles__title"
            style={{
              ...titlesStyles,
              position: "relative",
              top: isMobile ? 0 : "10px",
              textTransform:
                titlesStyles.textTransform === "capitalize"
                  ? "none"
                  : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
              marginTop: isGivenchy && isMobile ? "20px" : "",
              marginBottom: isGivenchy && !isMobile ? "20px" : "10px",
              fontSize: isGivenchy
                ? descriptionStyles.fontSize
                : titlesStyles.fontSize,
            }}
            dangerouslySetInnerHTML={{
              __html: isGivenchy
                ? t("cup", {
                    value: selectedCup?.size
                      ? selectedCup?.size?.toString()?.toUpperCase()
                      : t("Select", { ns: "components/intro" }),
                  })
                : titlesStyles.textTransform === "capitalize"
                  ? capitalizeFirstLetter(t("cup"))
                  : t("cup"),
            }}
          />
        </div>
        <div className="sizes-grid">
          {BASE_SIZES.find((v) => v.name === sizeCountry)?.cup_sizes.map(
            (row, index) => (
              <button
                key={row.size}
                ref={index === 0 ? firstCupRef : null}
                aria-label={`${t("cup")} ${row.size}`}
                className={`size-button ${selectedCup?.size === row.size ? "selected" : ""}`}
                onClick={() => setSelectedCup(row)}
                onMouseEnter={() => setHoveredCup(index.toString())}
                onMouseLeave={() => setHoveredCup(null)}
                style={Object.assign({
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width:
                    row?.size?.toString()?.length > 2
                      ? "auto"
                      : `calc(${
                          getBodyItemsStyles(
                            selectedCup?.size === row?.size
                              ? "focused"
                              : "unfocused",
                          ).padding
                        } * 3)`,
                  minWidth: "fit-content",
                  ...getBodyItemsStyles(
                    selectedCup?.size === row?.size ? "focused" : "unfocused",
                    hoveredCup === index.toString(),
                    selectedCup?.size === row?.size,
                  ),
                  ...(!isMobile && (isIntersport || isBlackstore)
                    ? { minHeight: "48px", minWidth: "48px" }
                    : {}),
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  aspectRatio:
                    row?.size?.toString()?.length > 2 ? "unset" : "1 / 1",
                })}
              >
                {row.size}
              </button>
            ),
          )}
        </div>
      </div>
      <button
        tabIndex={0}
        ref={continueButtonRef}
        type="button"
        disabled={
          isSubmitting ||
          typeof selectedSizeFR !== "number" ||
          typeof selectedCupFR !== "string"
        }
        className={`continue-button breasts-continue`}
        onMouseEnter={(e) => {
          if (isMobile) return;
          setHovered(true);
          if (
            !(
              typeof selectedSizeFR !== "number" ||
              typeof selectedCupFR !== "string"
            )
          ) {
            Object.assign(e.currentTarget.style, hoverStylesContinue);
          }
        }}
        onMouseLeave={(e) => {
          setHovered(false);
          if (
            !(
              typeof selectedSizeFR !== "number" ||
              typeof selectedCupFR !== "string"
            )
          ) {
            Object.assign(e.currentTarget.style, CTAStyles);
          }
        }}
        onClick={() => {
          nextStep();
        }}
        style={CTAStyles}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
          {isLingerie
            ? t("continue", { ns: "components/intro" })
            : isTheodore
              ? t("continue")
              : activeStylesContinue.textTransform === "capitalize"
                ? capitalizeFirstLetter(t("continue"))
                : t("continue")}

          {isIntersport && (
            <span style={arrowContainerStyle} aria-hidden>
              <ChevronRight style={iconStyle} />
            </span>
          )}
        </div>
      </button>
      {!isLingerie && !isVB && (
        <span
          role="button"
          tabIndex={0}
          className="breasts-skip"
          onClick={() => {
            skipResult();
            handleAnalytics("action", "bra_skip", null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
          style={{
            color: femaleBraScreenUI.skip.fontColor,
            fontWeight: femaleBraScreenUI.skip.fontWeight,
            fontSize: femaleBraScreenUI.skip.fontSize,
            textDecoration: femaleBraScreenUI.skip.fontStyle,
            textTransform: isZadig || isGivenchy ? "uppercase" : "none",
            letterSpacing: (femaleBraScreenUI.skip as any)?.letterSpacing,
            position:
              isGivenchyDesktop && !isSmallHeightDevice ? "fixed" : "relative",
            bottom: isGivenchyDesktop && !isSmallHeightDevice ? "120px" : 0,
            width:
              isGivenchyDesktop && !isSmallHeightDevice
                ? "calc(100% - 160px)"
                : "",
            marginBottom:
              isGivenchyDesktop && isSmallHeightDevice ? "80px" : "20px",
          }}
        >
          {t("skip")}
        </span>
      )}
      <MeasuredBy step={step} />
    </div>
  );
};

export default StepBreasts;
